import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

const Container = styled.div`
  ${theme.glassContainer.normal}
  color: ${theme.colors.text.primary};
  padding: 24px;
  margin-bottom: 20px;
  max-width: 500px;
  margin: 0 auto;
  font-family: ${theme.typography.fontFamily};
`;

const Title = styled.h3`
  margin: 0 0 12px 0;
  font-size: ${theme.typography.sizes.lg};
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.weights.semibold};
`;

const Message = styled.p`
  margin: 0;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.base};
`;

const Suggestions = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(147, 51, 234, 0.2);
`;

const SuggestionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const SuggestionList = styled.ul`
  margin: 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 6px;
    line-height: 1.4;
    color: ${theme.colors.text.secondary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const RetryButton = styled.button`
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: white;
  border: 1px solid rgba(147, 51, 234, 0.5);
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
  
  &:hover {
    background: linear-gradient(135deg, #9333ea 0%, #7e22ce 100%);
    transform: translateY(-1px);
  }
`;

const LogoutButton = styled.button`
  background: rgba(252, 165, 165, 0.3);
  color: #991b1b;
  border: 1px solid rgba(239, 68, 68, 0.4);
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
  
  &:hover {
    background: rgba(252, 165, 165, 0.5);
    transform: translateY(-1px);
  }
`;

const BackButton = styled.button`
  background: rgba(233, 213, 255, 0.5);
  color: ${theme.colors.text.primary};
  border: 1px solid rgba(147, 51, 234, 0.3);
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
  
  &:hover {
    background: rgba(233, 213, 255, 0.7);
    transform: translateY(-1px);
  }
`;

interface Props {
  error: string;
  platform?: string;
  onRetry?: () => void;
  onLogout?: () => void;
  onBack?: () => void;
}

export const ErrorMessage: React.FC<Props> = ({ error, platform, onRetry, onLogout, onBack }) => {
  const getSuggestions = () => {
    const commonSuggestions = [
      'Check that your League ID is correct',
      'Make sure the league exists and is accessible',
      'Try again in a few moments'
    ];

    switch (platform) {
      case 'espn':
        return [
          ...commonSuggestions,
          'If this is a private league, make sure you\'ve provided valid ESPN cookies',
          'ESPN cookies expire - you may need to get fresh ones from your browser',
          'Verify the season year is correct (2020 or later)'
        ];
      
      case 'yahoo':
        return [
          ...commonSuggestions,
          'Make sure your Yahoo access token is valid and not expired',
          'Yahoo requires OAuth authentication - check if you need to re-authenticate',
          'Verify your League ID is in the correct format (e.g., 414.l.123456)'
        ];
      
      case 'sleeper':
        return [
          ...commonSuggestions,
          'Sleeper League IDs are usually 18 digits long',
          'Check that the league is from the current or recent seasons'
        ];
      
      default:
        return commonSuggestions;
    }
  };

  const getErrorIcon = () => '⚠️';

  const isYahoo401 = platform === 'yahoo' && error.toLowerCase().includes('401');

  const handleLogout = () => {
    // Clear Yahoo tokens from localStorage
    localStorage.removeItem('yahoo_access_token');
    localStorage.removeItem('yahoo_leagues');
    // Call the onLogout prop if provided
    if (onLogout) {
      onLogout();
    } else {
      // Fallback: reload the page
      window.location.reload();
    }
  };

  return (
    <Container>
      <Title>
        {getErrorIcon()}
        Unable to Load League Data
      </Title>
      <Message>{error}</Message>
      
      <Suggestions>
        <SuggestionTitle>Try these solutions:</SuggestionTitle>
        <SuggestionList>
          {getSuggestions().map((suggestion, index) => (
            <li key={index}>{suggestion}</li>
          ))}
        </SuggestionList>
      </Suggestions>
      
      <ButtonContainer>
        {onBack && (
          <BackButton onClick={onBack}>
            ← Go Back
          </BackButton>
        )}
        {onRetry && (
          <RetryButton onClick={onRetry}>
            Try Again
          </RetryButton>
        )}
        {isYahoo401 && (
          <LogoutButton onClick={handleLogout}>
            Logout from Yahoo
          </LogoutButton>
        )}
      </ButtonContainer>
    </Container>
  );
};